/**
 * Ordem das etapas do projeto executivo no escritório.
 *
 * Ordem nova (decisão do dono, 28/10/2026): EXECUTIVO → REVISÃO → APROVAÇÃO.
 * Ordem antiga: EXECUTIVO → APROVAÇÃO → REVISÃO.
 *
 * A unidade escolhe em Configurações > Planilhas a partir de que data vale a
 * ordem nova (`revisao_antes_desde`). A data é comparada com o EXECUTIVO
 * REALIZADO da linha: o que terminou antes do corte acaba na ordem antiga,
 * então nenhum ambiente pula etapa nem aparece em duas filas na virada. Sem
 * data, tudo segue na ordem antiga.
 *
 * O DadosContext carimba a ordem em cada linha (`CAMPO_ORDEM`) uma vez, e
 * todo o resto lê o carimbo — assim Clientes, Relatórios, Mapa e as filas do
 * escritório nunca discordam. Linha sem carimbo = ordem antiga.
 *
 * A planilha só tem datas e o ERP só lê: "trava" aqui é aviso. Na ordem nova,
 * APROVAÇÃO digitada sem REVISÃO deixa o ambiente na fila de Revisão com o
 * selo "Fora de ordem" (e no sino), e ele não segue para o PPCP.
 *
 * Sem React — testável em isolamento.
 */
import { addDiasUteis } from "./calculos";
import { hasValue, isNo } from "./data";
import { dataLocalDe } from "./dinheiro";
import { F } from "./gerencialFields";
import { startOfDay, toDate } from "./status";

export const DIAS_UTEIS_LIMITE_REVISAO = 2;

/** Carimbo posto pelo DadosContext. Não é coluna da planilha. */
export const CAMPO_ORDEM = "__ordemExecutivo";

export const ORDEM = Object.freeze({ ANTIGA: "antiga", NOVA: "nova" });

export const ETAPA_EXECUTIVO = Object.freeze({
  ELABORACAO: "elaboracao",
  REVISAO: "revisao",
  APROVACAO: "aprovacao",
  CONCLUIDO: "concluido",
});

export const STATUS_FORA_DE_ORDEM = "Fora de ordem";

/**
 * Ordem que vale para a linha. O ambiente que ainda não terminou o executivo
 * segue a regra do dia de hoje: se o corte já passou, ele vai na ordem nova.
 */
export function ordemDaLinha(row, revisaoAntesDesde, hoje = new Date()) {
  // Data só ("2026-11-03") em meia-noite LOCAL — ADR-0059.
  const corte = dataLocalDe(revisaoAntesDesde);
  if (!corte) return ORDEM.ANTIGA;
  const referencia = toDate(row?.[F.EXECUTIVO_REALIZADO]) || hoje;
  return startOfDay(referencia) >= corte ? ORDEM.NOVA : ORDEM.ANTIGA;
}

/** Linhas com a ordem carimbada. Sem corte devolve o mesmo array (sem cópia). */
export function carimbarOrdem(rows, revisaoAntesDesde, hoje = new Date()) {
  if (!Array.isArray(rows) || !dataLocalDe(revisaoAntesDesde)) return rows;
  return rows.map((row) => ({ ...row, [CAMPO_ORDEM]: ordemDaLinha(row, revisaoAntesDesde, hoje) }));
}

export function usaOrdemNova(row) {
  return row?.[CAMPO_ORDEM] === ORDEM.NOVA;
}

/**
 * Revisão feita. OTIMIZAÇÃO preenchida sem REVISÃO digitada conta como
 * revisão implícita (pedidos antigos), a mesma convenção de sempre.
 */
export function revisaoFeita(row) {
  return hasValue(row?.[F.REVISAO]) || hasValue(row?.[F.OTIMIZACAO_DO_LOTE]);
}

/** Etapa do executivo em que o ambiente está (ignora o que vem antes dele). */
export function etapaDoExecutivo(row) {
  if (!hasValue(row?.[F.EXECUTIVO_REALIZADO])) return ETAPA_EXECUTIVO.ELABORACAO;
  const aprovado = hasValue(row[F.APROVACAO_DO_EXECUTIVO]);
  if (usaOrdemNova(row)) {
    if (!revisaoFeita(row)) return ETAPA_EXECUTIVO.REVISAO;
    if (!aprovado) return ETAPA_EXECUTIVO.APROVACAO;
    return ETAPA_EXECUTIVO.CONCLUIDO;
  }
  if (!aprovado) return ETAPA_EXECUTIVO.APROVACAO;
  if (!revisaoFeita(row)) return ETAPA_EXECUTIVO.REVISAO;
  return ETAPA_EXECUTIVO.CONCLUIDO;
}

export function estaEmRevisao(row) {
  return etapaDoExecutivo(row) === ETAPA_EXECUTIVO.REVISAO;
}

export function estaEmAprovacao(row) {
  return etapaDoExecutivo(row) === ETAPA_EXECUTIVO.APROVACAO;
}

/** Ordem nova com APROVAÇÃO digitada antes da REVISÃO. */
export function foraDeOrdem(row) {
  return usaOrdemNova(row) && hasValue(row?.[F.APROVACAO_DO_EXECUTIVO]) && !revisaoFeita(row);
}

/**
 * Ordem nova com as duas datas, mas a aprovação datada antes da revisão.
 * Já saiu do escritório: vira só selo na fila do PPCP.
 */
export function aprovadoAntesDaRevisao(row) {
  if (!usaOrdemNova(row)) return false;
  const aprovacao = toDate(row?.[F.APROVACAO_DO_EXECUTIVO]);
  const revisao = toDate(row?.[F.REVISAO]);
  return Boolean(aprovacao && revisao && startOfDay(aprovacao) < startOfDay(revisao));
}

/** Limite da revisão: executivo + 2 dias úteis. A ordem antiga não tinha limite. */
export function limiteDaRevisao(row) {
  if (!usaOrdemNova(row)) return null;
  const executivo = row?.[F.EXECUTIVO_REALIZADO];
  return hasValue(executivo) ? addDiasUteis(executivo, DIAS_UTEIS_LIMITE_REVISAO) : null;
}

/** Data em que o ambiente entrou na fila de Revisão. */
export function entradaNaRevisao(row) {
  return usaOrdemNova(row) ? row?.[F.EXECUTIVO_REALIZADO] : row?.[F.APROVACAO_DO_EXECUTIVO];
}

/** Data em que o ambiente entrou na fila de Aprovação. */
export function entradaNaAprovacao(row) {
  return usaOrdemNova(row) ? row?.[F.REVISAO] : row?.[F.EXECUTIVO_REALIZADO];
}

/**
 * Fila do PPCP: aprovado E revisado (REVISÃO digitada), sem otimização, fora
 * obra futura. Vale nas duas ordens — o PPCP sempre recebeu só o que passou
 * pelas duas etapas.
 */
export function estaNaFilaDoPpcp(row) {
  return (
    isNo(row?.[F.OBRA_FUTURA]) &&
    hasValue(row?.[F.EXECUTIVO_REALIZADO]) &&
    hasValue(row?.[F.APROVACAO_DO_EXECUTIVO]) &&
    hasValue(row?.[F.REVISAO]) &&
    !hasValue(row?.[F.OTIMIZACAO_DO_LOTE])
  );
}

/**
 * Lista de etapas (escrita na ordem nova) na ordem que vale para a linha: na
 * antiga, troca revisão e aprovação de lugar. Devolve a mesma lista quando
 * não há o que trocar.
 */
export function naOrdemDaLinha(row, etapas, { ehRevisao, ehAprovacao }) {
  if (usaOrdemNova(row)) return etapas;
  const indiceRevisao = etapas.findIndex(ehRevisao);
  const indiceAprovacao = etapas.findIndex(ehAprovacao);
  if (indiceRevisao < 0 || indiceAprovacao < 0 || indiceAprovacao < indiceRevisao) return etapas;
  const copia = [...etapas];
  copia[indiceRevisao] = etapas[indiceAprovacao];
  copia[indiceAprovacao] = etapas[indiceRevisao];
  return copia;
}
